import { Logger } from "@/utils/logger";

/**
 * Ably connection states relevant for reconnection decisions
 */
export type OfflineState = "disconnected" | "suspended" | null;

/**
 * Context passed from RealtimeClient when connection returns to CONNECTED
 */
export interface ReconnectContext {
  /** The previous connection state before CONNECTED */
  previousState: string;
  /** The last offline state (disconnected or suspended) before reconnection */
  lastOfflineState: OfflineState;
  /** Duration in milliseconds the connection was offline */
  offlineDurationMs: number;
  /** Ably connection ID after reconnection */
  connectionId?: string;
}

/**
 * Decision result from the coordinator
 */
export interface ReconnectDecision {
  /** Whether to run full snapshot resync */
  shouldResync: boolean;
  /** Reason for the decision (for logging) */
  reason: string;
}

/**
 * Callback function type for executing the resync pipeline
 */
export type ResyncPipelineCallback = (context: ReconnectContext) => Promise<void>;

/**
 * ReconnectCoordinator - Single source of truth for reconnection logic
 *
 * Aligns reconnect-resync with Ably automatic recovery behavior:
 * - DISCONNECTED: short disruption, Ably may resume continuity
 * - SUSPENDED: longer disruption, continuity is lost
 *
 * Decision rules:
 * 1. SUSPENDED → always run snapshot resync (bypasses MIN_INTERVAL throttle)
 * 2. DISCONNECTED + duration >= threshold → run snapshot resync (subject to MIN_INTERVAL)
 * 3. DISCONNECTED + duration < threshold → skip resync (Ably likely resumed)
 *
 * Note: This class is NOT a singleton - each manager should create its own instance.
 */
export class ReconnectCoordinator {
  /** Default threshold for offline duration before triggering resync */
  static readonly RESYNC_OFFLINE_THRESHOLD_MS = 2500;

  /** Minimum interval between resync operations (only for DISCONNECTED-threshold resyncs) */
  static readonly MIN_INTERVAL_BETWEEN_RESYNCS_MS = 5000;

  /** Mutex: current in-flight reconnection pipeline promise */
  private inFlight: Promise<void> | null = null;

  /** Timestamp of last successful resync */
  private lastResyncAt = 0;

  /** Whether coordinator is enabled */
  private enabled = true;

  /** Resync pipeline callback */
  private resyncCallback: ResyncPipelineCallback | null = null;

  /** Identifier for this coordinator instance (for logging) */
  private readonly instanceId: string;

  constructor(instanceId: string) {
    this.instanceId = instanceId;
    Logger.log(`[ReconnectCoordinator:${this.instanceId}] Initialized`);
  }

  /**
   * Set the resync pipeline callback
   * This callback will be invoked when coordinator decides resync is needed
   */
  setResyncCallback(callback: ResyncPipelineCallback): void {
    this.resyncCallback = callback;
  }

  /**
   * Check if a reconnection pipeline is currently running
   */
  get isRunning(): boolean {
    return this.inFlight !== null;
  }

  /**
   * Enable or disable the coordinator
   * When disabled, no resync will be triggered
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Make a decision on whether to run resync based on context
   * Returns decision plus whether to bypass MIN_INTERVAL throttle
   */
  private makeDecision(context: ReconnectContext): ReconnectDecision & { bypassMinInterval: boolean } {
    const { lastOfflineState, offlineDurationMs } = context;

    // Rule 1: SUSPENDED always requires resync (continuity lost) - bypasses MIN_INTERVAL
    if (lastOfflineState === "suspended") {
      return {
        shouldResync: true,
        reason: "Connection was SUSPENDED - continuity lost, snapshot required",
        bypassMinInterval: true,
      };
    }

    // Rule 2: DISCONNECTED + duration >= threshold → resync (subject to MIN_INTERVAL)
    if (lastOfflineState === "disconnected" && offlineDurationMs >= ReconnectCoordinator.RESYNC_OFFLINE_THRESHOLD_MS) {
      return {
        shouldResync: true,
        reason: `Offline duration (${offlineDurationMs}ms) exceeded threshold (${ReconnectCoordinator.RESYNC_OFFLINE_THRESHOLD_MS}ms)`,
        bypassMinInterval: false,
      };
    }

    // Rule 3: Short DISCONNECTED → skip resync (Ably likely resumed)
    if (lastOfflineState === "disconnected") {
      return {
        shouldResync: false,
        reason: `Short disconnection (${offlineDurationMs}ms < ${ReconnectCoordinator.RESYNC_OFFLINE_THRESHOLD_MS}ms) - Ably likely resumed`,
        bypassMinInterval: false,
      };
    }

    // No offline state recorded (should not happen in normal flow)
    return {
      shouldResync: false,
      reason: "No offline state recorded - skipping resync",
      bypassMinInterval: false,
    };
  }

  /**
   * Check if enough time has passed since last resync
   */
  private canResyncNow(): boolean {
    const now = Date.now();
    const timeSinceLastResync = now - this.lastResyncAt;
    return timeSinceLastResync >= ReconnectCoordinator.MIN_INTERVAL_BETWEEN_RESYNCS_MS;
  }

  /**
   * Main entry point: handle reconnection event from RealtimeClient
   *
   * This method is called when connection transitions TO CONNECTED after being offline.
   * It decides whether to run the resync pipeline based on:
   * - Previous offline state (SUSPENDED vs DISCONNECTED)
   * - Offline duration
   * - Time since last resync (only for DISCONNECTED-threshold, not SUSPENDED)
   *
   * Uses mutex to coalesce multiple CONNECTED events (network flapping)
   */
  async handleReconnection(context: ReconnectContext): Promise<void> {
    if (!this.enabled) {
      return;
    }

    // If pipeline is already running, wait for it to complete (coalesce)
    if (this.inFlight) {
      return this.inFlight;
    }

    // Make decision on whether to resync
    const decision = this.makeDecision(context);

    if (!decision.shouldResync) {
      return;
    }

    // Check minimum interval between resyncs (only for DISCONNECTED-threshold, not for SUSPENDED)
    if (!decision.bypassMinInterval && !this.canResyncNow()) {
      return;
    }

    // Run the resync pipeline
    await this.runPipeline(context, decision.reason);
  }

  /**
   * Force a resync regardless of decision rules
   * Use this for critical invariant violations or manual resync requests
   */
  async forceResync(context: ReconnectContext): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }

    await this.runPipeline(context, "Forced resync requested");
  }

  /**
   * Execute the resync pipeline with mutex protection
   */
  private async runPipeline(context: ReconnectContext, _reason: string): Promise<void> {
    if (!this.resyncCallback) {
      return;
    }
    const pipelinePromise = (async () => {
      try {
        await this.resyncCallback!(context);
        this.lastResyncAt = Date.now();
      } catch (error) {
        throw error;
      } finally {
        this.inFlight = null;
      }
    })();

    this.inFlight = pipelinePromise;
    return pipelinePromise;
  }

  /**
   * Reset coordinator state (e.g., on logout or session end)
   */
  reset(): void {
    this.inFlight = null;
    this.lastResyncAt = 0;
  }
}

// Note: No global singleton - each manager creates its own ReconnectCoordinator instance
